"use client";

import { ChevronUpDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { createRestDataProviderClient } from "@providers/data-provider/rest-data-provider.client";
import { THomeHeroSearchBar } from "@shared/cms";
import { transformQueryParamsWithoutLocale } from "@shared/utils";
import { Button, Combobox, ComboboxOption, Skeleton, useisDesktop } from "@ubud-ui/react-ui-kit";
import { useQuery } from "@tanstack/react-query";
import { useLayoutEffect, useRef, useState } from "react";

type Skill = {
  key: string;
  name: string;
};

type Region = {
  id: string;
  name: string;
  type: "province" | "city" | "country";
};

export type HomeHeroSearchBarDto = {
  skill?: ComboboxOption | null;
  location?:
    | (ComboboxOption & {
        type: string;
      })
    | null;
};
export function HomeHeroSearchBar({
  buttonLabel,
  skillsPlaceholder,
  skillsSearchPlaceholder,
  locationsPlaceholder,
  locationsSearchPlaceholder,
  onSubmit,
}: THomeHeroSearchBar & {
  onSubmit: (payload: HomeHeroSearchBarDto) => void;
}) {
  const isDesktop = useisDesktop();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number | undefined>();

  const restDataProviderClient = createRestDataProviderClient();

  useLayoutEffect(() => {
    if (containerRef.current) {
      const updateHeight = () => setContainerHeight(containerRef.current!.offsetHeight);
      updateHeight();

      window.addEventListener("resize", updateHeight);
      return () => {
        window.removeEventListener("resize", updateHeight);
      };
    }
  }, [containerRef]);

  const [selectedSkill, setSelectedSkill] = useState<ComboboxOption | null>(null);
  const [skillKeyword, setSkillKeyword] = useState<string>("");
  const { data: skillsResult } = useQuery({
    queryKey: ["home-hero-search-bar-skills-", skillKeyword],
    queryFn: () =>
      restDataProviderClient.custom({
        url: transformQueryParamsWithoutLocale("/karirhub/v2/skills", {
          limit: 5,
          keyword: skillKeyword || null,
        }),
        method: "GET",
      }),
  });
  const skillOptions: ComboboxOption[] =
    skillsResult?.data?.map((skill: Skill) => ({
      value: skill.key,
      label: skill.name,
    })) ?? [];

  const handleSearchSkills = (value: string) => {
    setSkillKeyword(value);
  };
  const handleChangeSkill = (val: ComboboxOption | null) => {
    setSelectedSkill(val);
    setSkillKeyword(val?.label || "");
  };

  const [selectedLocation, setSelectedLocation] = useState<ComboboxOption | null>(null);
  const [locationKeyword, setLocationKeyword] = useState<string>("");
  const { data: provincesResult } = useQuery({
    queryKey: ["home-hero-search-bar-provinces-", locationKeyword],
    queryFn: () =>
      restDataProviderClient.custom({
        url: transformQueryParamsWithoutLocale(
          "/karirhub/onboarding/v2/provinces",
          locationKeyword
            ? {
                keyword: locationKeyword,
                limit: 10,
              }
            : null,
        ),
        method: "GET",
      }),
  });
  const { data: citiesResult } = useQuery({
    queryKey: ["home-hero-search-bar-cities-", locationKeyword],
    queryFn: () =>
      restDataProviderClient.custom({
        url: transformQueryParamsWithoutLocale(
          "/karirhub/onboarding/v2/cities",
          locationKeyword
            ? {
                keyword: locationKeyword,
                limit: 10,
              }
            : null,
        ),
        method: "GET",
      }),
  });
  const locationOptions: ComboboxOption[] =
    [
      ...(provincesResult?.data || []).map((it: Region) => {
        return { ...it, type: "province" };
      }),
      ...(citiesResult?.data || []).map((it: Region) => {
        return { ...it, type: "city" };
      }),
    ].map((region: Region) => ({
      value: `${region.id}#${region.type}`,
      label: region.name,
    })) ?? [];
  const handleChangeLocation = (option: ComboboxOption | null) => {
    setSelectedLocation(option);
    setLocationKeyword(option?.label || "");
  };
  const handleSearchLocations = (keyword: string) => {
    setLocationKeyword(keyword);
  };

  const handleSubmit = () => {
    if (selectedSkill || selectedLocation) {
      const locationValues = selectedLocation && selectedLocation.value ? selectedLocation.value.split("#") : [];

      const payload: HomeHeroSearchBarDto = {
        skill: selectedSkill || null,
        location: selectedLocation
          ? {
              label: selectedLocation?.label || "",
              type: locationValues && locationValues.length > 0 ? locationValues[1] : null,
              value: locationValues && locationValues.length > 0 ? locationValues[0] : null,
            }
          : null,
      };

      onSubmit(payload);
    }
  };

  return (
    <>
      <div className="relative">
        <div
          className="flex items-stretch bg-white rounded-lg overflow-hidden border border-gray-200 flex-col lg:flex-row"
          ref={containerRef}
        >
          <Combobox
            options={skillOptions}
            customableSearch
            searchable
            clearable
            onSearch={handleSearchSkills}
            onChange={handleChangeSkill}
            onClear={() => handleChangeSkill(null)}
            popoverContentProps={{
              style: { maxHeight: "300px", overflowY: "auto", marginTop: !isDesktop ? "-78px" : "-60px" },
            }}
            inputProps={{
              className: "px-2 text-base",
            }}
            placeholder={skillsPlaceholder}
            searchPlaceholder={skillsSearchPlaceholder}
            prependIcon={<MagnifyingGlassIcon className="size-5 ml-1" />}
            buttonProps={{
              style: { height: !isDesktop ? containerHeight + "px" : "auto" },
              className: "w-full rounded-none border-0",
            }}
          />
          <div
            className="bg-gray-100 w-full min-w-full lg:w-px lg:min-w-px"
            style={{ height: !isDesktop ? containerHeight + "px" : "1px" }}
          >
            &nbsp;
          </div>
          <Combobox
            options={locationOptions}
            initialValue={selectedLocation}
            searchable
            clearable
            onSearch={handleSearchLocations}
            onChange={handleChangeLocation}
            onClear={() => handleChangeLocation(null)}
            searchPlaceholder={locationsSearchPlaceholder}
            placeholder={locationsPlaceholder}
            inputProps={{
              className: "px-2 text-base",
            }}
            buttonProps={{
              className: "w-full lg:w-[55%] rounded-none border-0",
              style: { height: !isDesktop ? containerHeight + "px" : "auto" },
            }}
            popoverContentProps={{
              style: { maxHeight: "300px", overflowY: "auto", marginTop: !isDesktop ? "-78px" : "-60px" },
            }}
            appendIcon={<ChevronUpDownIcon className="size-5" />}
          />
          <div
            className="bg-gray-100 w-full min-w-full lg:w-px lg:min-w-px"
            style={{ height: !isDesktop ? containerHeight + "px" : "1px" }}
          >
            &nbsp;
          </div>
          <div className="py-3 px-4">
            <Button
              className="font-bold px-6 rounded-lg py-6 text-sm lg:text-base w-full"
              disabled={!(skillKeyword || selectedLocation)}
              onClick={handleSubmit}
            >
              {buttonLabel}
            </Button>
          </div>
        </div>
        {containerHeight === undefined && (
          <div className="absolute top-0 left-0 w-full h-full z-10 border bg-white border-gray-200 rounded-lg overflow-hidden">
            <Skeleton className="w-full h-full" />
          </div>
        )}
      </div>
    </>
  );
}
